// 店舗 予算作成ページ (2026-06-12)
//   室長が半期ごとに予算を入力。原本Exc(損益計算書/労務費/明細)のドライバーモデルを分かりやすく1画面化。
//   入力=原価率・営業利益目標(月別) + 労務費(給与/平均時給×勤務時間=雑給/法定福利費/福利厚生費/通勤費/募集費/研修費)
//        + 販売費(勘定別月別)。派生=売上高/原価/売上総利益/一般管理費/損益分岐点/人時売上。
//   前年同月実績は freee から自動取込し各セルに薄字併記(「前年複写」で予算へ複写)。
//   確定で budget_monthly(source='budget_create')へ反映 → 部門別予算実績ページの予算列に即連動。
//   絵文字禁止 / react-icons/md のみ / @freee_jp/vibes 準拠。

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, PageTitle, CardBase, HStack, Button, Text, Note,
  Breadcrumbs, MessageBlock, InlineSpinner,
} from '@freee_jp/vibes';
import { MdEditNote, MdRefresh, MdSave, MdCheckCircle, MdContentCopy, MdLockOpen } from 'react-icons/md';
import {
  Half, monthsForHalf, FISCAL_MONTH_CAL, LABOR_DIRECT_ITEMS, SELLING_ITEMS,
  fetchBudgetCreate, saveBudgetInput, submitBudget, reopenBudget, fetchPrevActual,
  BudgetMonthlyRow, BudgetInputRow,
} from '../api/budgetCreate';

const API_BASE = 'https://invoice-review.system-0f7.workers.dev';
const TERMS = [81, 80, 79];

interface SectionOpt { section_id: number; name: string; long_name: string; available?: number }

const yen = (v: number) => (Number.isFinite(v) ? Math.round(v).toLocaleString('ja-JP') : '0');

const S = {
  page: { background: '#f3f1ef', minHeight: '100vh', paddingBottom: '3rem' } as React.CSSProperties,
  block: { background: '#fff', borderRadius: 8, padding: 14, marginTop: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' } as React.CSSProperties,
  th: { border: '1px solid #e2e0de', background: '#f5f4f2', padding: '4px 6px', textAlign: 'center', fontWeight: 600, whiteSpace: 'nowrap' } as React.CSSProperties,
  tdName: { border: '1px solid #e2e0de', padding: '2px 8px', whiteSpace: 'nowrap', fontWeight: 500 } as React.CSSProperties,
  td: { border: '1px solid #e2e0de', padding: '1px 2px', textAlign: 'right' } as React.CSSProperties,
  tdDerived: { border: '1px solid #e2e0de', padding: '2px 6px', textAlign: 'right', background: '#fafafa', color: '#374151' } as React.CSSProperties,
  inp: { width: '100%', border: '1px solid #d4d2d0', borderRadius: 3, padding: '2px 4px', textAlign: 'right', fontSize: '0.8rem', boxSizing: 'border-box' } as React.CSSProperties,
  prev: { fontSize: '0.62rem', color: '#9ca3af', textAlign: 'right', lineHeight: 1.1 } as React.CSSProperties,
};
const SEL: React.CSSProperties = { padding: '5px 8px', border: '1px solid #d4d2d0', borderRadius: 4 };

type NumMap = Record<string, Record<number, number>>;

export const BudgetCreatePage: React.FC = () => {
  const [sections, setSections] = useState<SectionOpt[]>([]);
  const [sectionId, setSectionId] = useState<number>(3068544);
  const [term, setTerm] = useState<number>(80);
  const [half, setHalf] = useState<Half>('H2');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('none');
  const [version, setVersion] = useState<number>(0);

  // 入力 state
  const [driver, setDriver] = useState<NumMap>({});      // cost_rate(%表示) / op_profit / work_hours_emp / work_hours_ap
  const [avgWage, setAvgWage] = useState<number>(1350);
  const [item, setItem] = useState<NumMap>({});          // display_item(給与手当/法定福利費/.../販売費勘定) -> fm -> 金額
  const [prev, setPrev] = useState<Map<string, Map<number, number>>>(new Map());
  const [prevLoading, setPrevLoading] = useState(false);

  const months = monthsForHalf(half);

  useEffect(() => {
    fetch(`${API_BASE}/api/sections`).then((r) => r.json()).then((d) => {
      const arr: SectionOpt[] = Array.isArray(d) ? d : (d.sections || d.rows || []);
      setSections(arr.filter((s) => s.available !== 0 && s.section_id && s.section_id !== 0));
    }).catch(() => { /* */ });
  }, []);

  const storeName = useCallback((): string => {
    const s = sections.find((x) => x.section_id === sectionId);
    return s ? s.long_name : `部門${sectionId}`;
  }, [sectionId, sections]);

  const getD = (k: string, fm: number) => driver[k]?.[fm] ?? 0;
  const setD = (k: string, fm: number, v: number) => setDriver((p) => ({ ...p, [k]: { ...(p[k] || {}), [fm]: v } }));
  const getI = (it: string, fm: number) => item[it]?.[fm] ?? 0;
  const setI = (it: string, fm: number, v: number) => setItem((p) => ({ ...p, [it]: { ...(p[it] || {}), [fm]: v } }));
  const prevOf = (it: string, fm: number) => prev.get(it)?.get(fm) ?? null;

  // --- 派生計算(月別) ---
  const calc = useMemo(() => {
    const zatsu: Record<number, number> = {};   // 雑給 = 平均時給 × 勤務時間数(AP)
    const labor: Record<number, number> = {};
    const selling: Record<number, number> = {};
    const sga: Record<number, number> = {};
    const sales: Record<number, number> = {};
    const cogs: Record<number, number> = {};
    const gross: Record<number, number> = {};
    const breakeven: Record<number, number> = {};
    const jinji: Record<number, number> = {};
    for (const fm of months) {
      zatsu[fm] = avgWage * getD('work_hours_ap', fm);
      const laborSum = LABOR_DIRECT_ITEMS.reduce((s, it) => s + getI(it, fm), 0) + zatsu[fm];
      labor[fm] = laborSum;
      const sellSum = SELLING_ITEMS.reduce((s, it) => s + getI(it, fm), 0);
      selling[fm] = sellSum;
      sga[fm] = laborSum + sellSum;
      const cr = getD('cost_rate', fm) / 100; // %→比率
      const op = getD('op_profit', fm);
      sales[fm] = cr < 1 ? (sga[fm] + op) / (1 - cr) : 0;
      cogs[fm] = sales[fm] * cr;
      gross[fm] = sales[fm] - cogs[fm];
      breakeven[fm] = cr < 1 ? sga[fm] / (1 - cr) : 0;
      const wh = getD('work_hours_emp', fm) + getD('work_hours_ap', fm);
      jinji[fm] = wh ? sales[fm] / wh : 0;
    }
    return { zatsu, labor, selling, sga, sales, cogs, gross, breakeven, jinji };
  }, [driver, avgWage, item, months]);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setMsg(null);
    try {
      const d = await fetchBudgetCreate(term, sectionId, half);
      const dr: NumMap = {}; let aw = 1350;
      for (const r of d.inputs) {
        if (r.input_key === 'avg_wage') { aw = r.value; continue; }
        dr[r.input_key] = dr[r.input_key] || {};
        dr[r.input_key][r.fiscal_month] = r.input_key === 'cost_rate' ? r.value * 100 : r.value; // 比率→%表示
      }
      const it: NumMap = {};
      for (const r of d.budget) {
        if (r.display_item === '売上' || r.display_item === '材料仕入' || r.display_item === '雑給') continue; // 派生は復元不要
        it[r.display_item] = it[r.display_item] || {};
        it[r.display_item][r.fiscal_month] = r.amount;
      }
      setDriver(dr); setAvgWage(aw); setItem(it);
      setStatus(d.submission.status); setVersion(d.submission.version);
      // 前年実績 自動取込
      setPrevLoading(true);
      fetchPrevActual(term, sectionId, half).then(setPrev).catch(() => setPrev(new Map())).finally(() => setPrevLoading(false));
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [term, sectionId, half]);

  // 前年実績を予算へ複写(費用勘定のみ。空でも上書き)
  const copyPrev = useCallback(() => {
    const next: NumMap = { ...item };
    for (const it of [...LABOR_DIRECT_ITEMS, ...SELLING_ITEMS]) {
      next[it] = { ...(next[it] || {}) };
      for (const fm of months) next[it][fm] = prevOf(it, fm) ?? 0;
    }
    setItem(next);
    setMsg('前年同月実績を予算欄へ複写しました（必要に応じて調整してください）。');
  }, [item, prev, months]);

  // 保存用 budget_rows(確定 display_item × 月) + inputs
  const buildPayload = useCallback(() => {
    const budget_rows: BudgetMonthlyRow[] = [];
    const inputs: BudgetInputRow[] = [];
    for (const fm of months) {
      budget_rows.push({ display_item: '売上', fiscal_month: fm, amount: Math.round(calc.sales[fm]) });
      budget_rows.push({ display_item: '材料仕入', fiscal_month: fm, amount: Math.round(calc.cogs[fm]) });
      budget_rows.push({ display_item: '雑給', fiscal_month: fm, amount: Math.round(calc.zatsu[fm]) });
      for (const it of LABOR_DIRECT_ITEMS) budget_rows.push({ display_item: it, fiscal_month: fm, amount: Math.round(getI(it, fm)) });
      for (const it of SELLING_ITEMS) budget_rows.push({ display_item: it, fiscal_month: fm, amount: Math.round(getI(it, fm)) });
      for (const k of ['cost_rate', 'op_profit', 'work_hours_emp', 'work_hours_ap']) {
        inputs.push({ input_key: k, fiscal_month: fm, value: k === 'cost_rate' ? getD(k, fm) / 100 : getD(k, fm) });
      }
    }
    inputs.push({ input_key: 'avg_wage', fiscal_month: 0, value: avgWage });
    return { budget_rows, inputs };
  }, [calc, months, item, driver, avgWage]);

  const save = useCallback(async () => {
    setBusy(true); setError(null); setMsg(null);
    try {
      const { budget_rows, inputs } = buildPayload();
      const r = await saveBudgetInput({ fiscal_term: term, section_id: sectionId, half, inputs, budget_rows, updated_by: 'web_app_user' });
      setStatus('draft');
      setMsg(`下書き保存しました（予算 ${r.budget_saved} 件）。部門別予算実績ページに即反映されます。`);
    } catch (e: any) { setError(`保存失敗: ${e.message || e}`); } finally { setBusy(false); }
  }, [buildPayload, term, sectionId, half]);

  const doSubmit = useCallback(async () => {
    setBusy(true); setError(null); setMsg(null);
    try {
      const { budget_rows, inputs } = buildPayload();
      await saveBudgetInput({ fiscal_term: term, section_id: sectionId, half, inputs, budget_rows, updated_by: 'web_app_user' });
      const r = await submitBudget(term, sectionId, half, 'web_app_user');
      setStatus('submitted'); setVersion(r.version);
      setMsg(`予算を確定しました（v${r.version}）。`);
    } catch (e: any) { setError(`確定失敗: ${e.message || e}`); } finally { setBusy(false); }
  }, [buildPayload, term, sectionId, half]);

  const doReopen = useCallback(async () => {
    setBusy(true); setError(null);
    try { await reopenBudget(term, sectionId, half); setStatus('draft'); setMsg('下書きに差し戻しました。'); }
    catch (e: any) { setError(`差戻し失敗: ${e.message || e}`); } finally { setBusy(false); }
  }, [term, sectionId, half]);

  // --- セル(入力 + 前年薄字) ---
  const InputCell = ({ it, fm, pct }: { it: string; fm: number; pct?: boolean }) => {
    const v = pct ? getD(it, fm) : getI(it, fm);
    const onChange = (val: number) => pct ? setD(it, fm, val) : setI(it, fm, val);
    const p = pct ? null : prevOf(it, fm);
    return (
      <td style={S.td}>
        <input type="number" style={S.inp} value={v === 0 ? '' : v}
          onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
          placeholder={p != null ? String(Math.round(p)) : '0'} />
        {p != null && <div style={S.prev}>前年 {yen(p)}</div>}
      </td>
    );
  };
  const DerivedRow = ({ label, vals, bold }: { label: string; vals: Record<number, number>; bold?: boolean }) => (
    <tr>
      <td style={{ ...S.tdName, fontWeight: bold ? 700 : 500, background: bold ? '#f0f4ff' : undefined }}>{label}</td>
      {months.map((fm) => <td key={fm} style={{ ...S.tdDerived, fontWeight: bold ? 700 : 400 }}>{yen(vals[fm] || 0)}</td>)}
      <td style={{ ...S.tdDerived, fontWeight: 700 }}>{yen(months.reduce((s, fm) => s + (vals[fm] || 0), 0))}</td>
    </tr>
  );
  const monthHeader = (
    <tr>
      <th style={S.th}>項目</th>
      {months.map((fm) => <th key={fm} style={S.th}>{FISCAL_MONTH_CAL[fm - 1]}<div style={{ fontSize: '0.6rem', color: '#9ca3af' }}>({fm}月度)</div></th>)}
      <th style={S.th}>計</th>
    </tr>
  );
  const sumItem = (it: string) => months.reduce((s, fm) => s + getI(it, fm), 0);

  const submitted = status === 'submitted';

  return (
    <div style={S.page}>
      <Container>
        <Breadcrumbs links={[{ url: '#', title: 'FREEEMAN' }, { url: '#budget_create', title: '店舗予算作成' }]} />
        <div style={{ marginTop: 16, marginBottom: 12 }}>
          <HStack gap={2} alignItems="center">
            <MdEditNote size={28} color="#2563eb" />
            <PageTitle>店舗 予算作成（半期）</PageTitle>
          </HStack>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Note>
            店舗ごとに半期（上期=10〜3月／下期=4〜9月）の予算を作成します。原価率・営業利益目標と労務費・販売費を入力すると、売上高・損益分岐点が自動計算されます。
            前年同月実績は freee から自動取込し各欄に薄字で表示（「前年複写」で予算へ複写）。確定すると部門別予算実績ページの予算列に反映されます。
          </Note>
        </div>

        <CardBase>
          <div style={{ padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <select value={sectionId} onChange={(e) => setSectionId(Number(e.target.value))} style={SEL} title="店舗">
                {sections.map((s) => <option key={s.section_id} value={s.section_id}>{s.long_name}</option>)}
              </select>
              <select value={term} onChange={(e) => setTerm(Number(e.target.value))} style={SEL} title="会計期">
                {TERMS.map((t) => <option key={t} value={t}>{t}期</option>)}
              </select>
              <select value={half} onChange={(e) => setHalf(e.target.value as Half)} style={SEL} title="半期">
                <option value="H1">上期（10〜3月）</option>
                <option value="H2">下期（4〜9月）</option>
              </select>
              <Button appearance="primary" onClick={load} disabled={loading}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 56, justifyContent: 'center' }}>
                  {loading ? <InlineSpinner isLoading={true} /> : <MdRefresh size={16} />} 読込
                </span>
              </Button>
              <Button onClick={copyPrev} disabled={loading || prevLoading || prev.size === 0}>
                <MdContentCopy /> 前年複写
              </Button>
              <Button appearance="primary" onClick={save} disabled={busy || loading}>
                {busy ? <InlineSpinner isLoading={true} /> : <MdSave />} 下書き保存
              </Button>
              <Button onClick={doSubmit} disabled={busy || loading}>
                <MdCheckCircle /> 確定
              </Button>
              {submitted && <Button onClick={doReopen} disabled={busy}><MdLockOpen /> 差戻し</Button>}
              <span style={{ padding: '4px 10px', borderRadius: 12, fontSize: '0.78rem',
                background: submitted ? '#dcfce7' : status === 'draft' ? '#fef9c3' : '#f1f5f9',
                color: submitted ? '#166534' : status === 'draft' ? '#854d0e' : '#64748b' }}>
                {submitted ? `確定済 v${version}` : status === 'draft' ? '下書き' : '未作成'}
              </span>
              {prevLoading && <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>前年実績を取得中…</span>}
            </div>
            {error && <div style={{ marginTop: 12 }}><MessageBlock success={false}>{error}</MessageBlock></div>}
            {msg && <div style={{ marginTop: 12 }}><MessageBlock success={true}>{msg}</MessageBlock></div>}
          </div>
        </CardBase>

        {/* ブロック1 ドライバー */}
        <div style={S.block}>
          <Text weight="bold">① 売上ドライバー（原価率・営業利益目標を入力 → 売上高を逆算）</Text>
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={S.table}>
              <thead>{monthHeader}</thead>
              <tbody>
                <tr><td style={S.tdName}>原価率（%）</td>{months.map((fm) => <InputCell key={fm} it="cost_rate" fm={fm} pct />)}<td style={S.tdDerived}></td></tr>
                <tr><td style={S.tdName}>営業利益 目標（¥）</td>{months.map((fm) => <InputCell key={fm} it="op_profit" fm={fm} pct />)}<td style={S.tdDerived}>{yen(months.reduce((s, fm) => s + getD('op_profit', fm), 0))}</td></tr>
                <DerivedRow label="売上高（自動）" vals={calc.sales} bold />
                <DerivedRow label="原価／材料仕入（自動）" vals={calc.cogs} />
                <DerivedRow label="売上総利益（自動）" vals={calc.gross} />
                <DerivedRow label="損益分岐点（自動）" vals={calc.breakeven} />
                <DerivedRow label="人時売上（自動）" vals={calc.jinji} />
              </tbody>
            </table>
          </div>
        </div>

        {/* ブロック2 労務費 */}
        <div style={S.block}>
          <Text weight="bold">② 労務費（社員給与・勤務時間・各費目を入力。雑給＝平均時給×AP勤務時間で自動）</Text>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 6 }}>
            <span style={{ fontSize: '0.8rem' }}>平均時給（半期共通）¥</span>
            <input type="number" style={{ ...S.inp, width: 90 }} value={avgWage === 0 ? '' : avgWage} onChange={(e) => setAvgWage(e.target.value === '' ? 0 : Number(e.target.value))} />
          </div>
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={S.table}>
              <thead>{monthHeader}</thead>
              <tbody>
                <tr><td style={S.tdName}>給与手当（社員）</td>{months.map((fm) => <InputCell key={fm} it="給与手当" fm={fm} />)}<td style={S.tdDerived}>{yen(sumItem('給与手当'))}</td></tr>
                <tr><td style={S.tdName}>勤務時間数（社員）</td>{months.map((fm) => <InputCell key={fm} it="work_hours_emp" fm={fm} pct />)}<td style={S.tdDerived}>{yen(months.reduce((s, fm) => s + getD('work_hours_emp', fm), 0))}</td></tr>
                <tr><td style={S.tdName}>勤務時間数（AP）</td>{months.map((fm) => <InputCell key={fm} it="work_hours_ap" fm={fm} pct />)}<td style={S.tdDerived}>{yen(months.reduce((s, fm) => s + getD('work_hours_ap', fm), 0))}</td></tr>
                <DerivedRow label="雑給（=時給×AP時間 自動）" vals={calc.zatsu} />
                <tr><td style={S.tdName}>法定福利費</td>{months.map((fm) => <InputCell key={fm} it="法定福利費" fm={fm} />)}<td style={S.tdDerived}>{yen(sumItem('法定福利費'))}</td></tr>
                <tr><td style={S.tdName}>福利厚生費</td>{months.map((fm) => <InputCell key={fm} it="福利厚生費" fm={fm} />)}<td style={S.tdDerived}>{yen(sumItem('福利厚生費'))}</td></tr>
                <tr><td style={S.tdName}>通勤費</td>{months.map((fm) => <InputCell key={fm} it="通勤費" fm={fm} />)}<td style={S.tdDerived}>{yen(sumItem('通勤費'))}</td></tr>
                <tr><td style={S.tdName}>募集費</td>{months.map((fm) => <InputCell key={fm} it="募集費" fm={fm} />)}<td style={S.tdDerived}>{yen(sumItem('募集費'))}</td></tr>
                <tr><td style={S.tdName}>研修費</td>{months.map((fm) => <InputCell key={fm} it="研修費" fm={fm} />)}<td style={S.tdDerived}>{yen(sumItem('研修費'))}</td></tr>
                <DerivedRow label="労務費 計（自動）" vals={calc.labor} bold />
              </tbody>
            </table>
          </div>
        </div>

        {/* ブロック3 販売費 */}
        <div style={S.block}>
          <Text weight="bold">③ 販売費（勘定別に月別入力）</Text>
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={S.table}>
              <thead>{monthHeader}</thead>
              <tbody>
                {SELLING_ITEMS.map((it) => (
                  <tr key={it}>
                    <td style={S.tdName}>{it}</td>
                    {months.map((fm) => <InputCell key={fm} it={it} fm={fm} />)}
                    <td style={S.tdDerived}>{yen(sumItem(it))}</td>
                  </tr>
                ))}
                <DerivedRow label="販売費 計（自動）" vals={calc.selling} bold />
                <DerivedRow label="一般管理費 計（労務費＋販売費 自動）" vals={calc.sga} bold />
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 10, fontSize: '0.78rem', color: '#6b7280' }}>
            営業利益 = 売上総利益 − 一般管理費。原価率・営業利益目標から逆算した売上高を①で確認してください。
          </div>
        </div>
      </Container>
    </div>
  );
};

export default BudgetCreatePage;
